import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BasicSearchProps } from './types';
import { GRAY_300, GRAY_500 } from 'theme/grayScale';

const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_PERF_LOG_TAG = '[PELS-CLIENT-PERF]';
const SHOULD_LOG_SEARCH_PERF =
  process.env.REACT_APP_ENV === 'stage' ||
  process.env.REACT_APP_ENV === 'development';

function serializeSearchPerfPayload(payload: Record<string, unknown>): string {
  try {
    return JSON.stringify(payload);
  } catch {
    return JSON.stringify({ error: 'Failed to serialize search perf payload' });
  }
}

function logBasicSearchEmit(
  trigger: 'debounce' | 'enter' | 'clear',
  term: string
) {
  if (!SHOULD_LOG_SEARCH_PERF) return;

  const payload = serializeSearchPerfPayload({
    trigger,
    term,
    termLength: term.length,
    debounceDelayMs: DEBOUNCE_DELAY,
    emittedAt: new Date().toISOString(),
  });

  console.info(
    `${SEARCH_PERF_LOG_TAG}[SearchPerf][BasicSearchEmit] ${payload}`
  );
}

const BasicSearch: React.FC<BasicSearchProps> = ({
  searchTerm,
  placeholder = 'Search...',
  onSearchTermChange,
  onClearSearch,
}) => {
  const [inputValue, setInputValue] = useState(searchTerm);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleClearSearch = useCallback(() => {
    setInputValue('');

    logBasicSearchEmit('clear', '');
    onSearchTermChange('');
    if (onClearSearch) {
      onClearSearch();
    }
  }, [onSearchTermChange, onClearSearch]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      const trimmedValue = value.trim();
      // Only emit if: empty (clear) OR length >= MIN_SEARCH_LENGTH
      if (
        trimmedValue.length === 0 ||
        trimmedValue.length >= MIN_SEARCH_LENGTH
      ) {
        logBasicSearchEmit('debounce', value);
        onSearchTermChange(value);
      }
    }, DEBOUNCE_DELAY);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      // Enter always triggers search, regardless of length
      logBasicSearchEmit('enter', inputValue);
      onSearchTermChange(inputValue);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <div style={styles.searchInputWrapperStyle}>
      <input
        type="text"
        placeholder={placeholder}
        style={styles.searchInputStyle}
        value={inputValue}
        onChange={e => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {inputValue && (
        <button onClick={handleClearSearch} style={styles.clearButtonStyle}>
          <X size={16} />
        </button>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchInputWrapperStyle: {
    position: 'relative',
    width: '100%',
  } as React.CSSProperties,
  searchInputStyle: {
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    padding: '0 0.5rem',
    width: '100%',
    height: '1.875rem',
    boxSizing: 'border-box',
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  clearButtonStyle: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: GRAY_500,
    fontSize: '1rem',
  } as React.CSSProperties,
};

export default BasicSearch;
